import { faChevronUp as ExpandIcon } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { sortBy, startCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';

import AssetPanelItemWrapper from '@/components/AssetPanelItemWrapper/AssetPanelItemWrapper';
import CheckRequirements from '@/components/CheckRequirements/CheckRequirements';
import InlineEditAutocomplete from '@/components/InlineEditAutocomplete';
import KeyValueResponsive from '@/components/KeyValueResponsive';
import MultiPartScoreBadge from '@/components/MultiPartScoreBadge';
import { PERMISSIONS } from '@/constants/auth.constants';
import {
  dataProviderDetailsFields
  // getNetworkAdapters
} from '@/features/AssetPanel';
import AssetProviderLogo from '@/features/AssetProviderLogo/AssetProviderLogo';
import useFetchOrganizationAssetTags from '@/hooks/useAssetTags';
import useGridTheme from '@/hooks/useGridTheme';
import { usePermissionsCheck } from '@/hooks/usePermissionsCheck';
import Panel from '@/layout/Panel';
import {
  useCreateAssetTag,
  useDeleteAssetTag
} from '@/pages/Asset/Asset.store';
import {
  ASSET_TOOLTIP_TEXT,
  type Asset,
  type AssetExternalDetailDTO,
  assetRiskScoreDialogAtom,
  selectedAssetAtom,
  useAssetSelection,
  useFetchAssetServices
} from '@/pages/Assets';
import {
  ASSET_DATA_PROVIDER_LABELS,
  formatHostIsolationExclusionReasons
} from '@/pages/Assets/Assets.constants';
import {
  extractTagNames,
  findTagByName,
  scorePartsNoTotal
} from '@/pages/Assets/Assets.utils';

import AssetActionsButton from '../AssetActionsButton';
import { AssetAddEditDialog } from '../AssetAddEditDialog';
import { COMPLIANCE_OPTIONS } from '../AssetAddEditDialog/AssetAddEditDialog.constants';
import {
  // NETWORK_ADAPTER_COLUMNS,
  RUNNING_SERVICES_COLUMNS
} from './AssetPanel.constants';
import useStyles from './AssetPanel.styles';

// TODO: Add in network adapters table when we have enough data
const AssetPanel = ({
  notAssetDetailsPage = false
}: {
  notAssetDetailsPage?: boolean;
}) => {
  const { deselectAsset } = useAssetSelection();
  const { data: runningServices, isLoading: isRunningServicesLoading } =
    useFetchAssetServices();
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const [, setAssetRiskScoreDialog] = useAtom(assetRiskScoreDialogAtom);
  const { data: organizationAssetTags } = useFetchOrganizationAssetTags(
    selectedAsset?.organization_id || 0
  );
  const { mutate: createAssetTag } = useCreateAssetTag();
  const { mutate: deleteAssetTag } = useDeleteAssetTag();
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const gridTheme = useGridTheme();
  const { classes } = useStyles();

  const protectedAttributes = selectedAsset?.protected_attributes || [];

  const externalDetails = sortBy(
    selectedAsset?.external_details || ([] as AssetExternalDetailDTO[]),
    'provider'
  );

  const {
    asset_name = '',
    description = '',
    compliance_tags = [],
    group_names = [],
    user_tags = [],
    ip_mapping_tags = [],
    host_isolation_exclusion_reasons = [],
    risk_score = 0,
    risk_score_details = {}
  } = selectedAsset || {};

  const servicesTableTheme = gridTheme.withParams({
    wrapperBorderRadius: 0,
    wrapperBorder: true
  });

  const handleClose = () => {
    deselectAsset();
  };

  // Extract tag names from the relationship objects
  const userTagNames = extractTagNames(user_tags);
  const complianceTagNames = extractTagNames(compliance_tags);
  const groupNameTags = extractTagNames(group_names);
  const ipMappingTagNames = extractTagNames(ip_mapping_tags);

  // Get user tag options from organization tags (exclude compliance tags)
  const userTagOptions =
    organizationAssetTags
      ?.filter((tag) => tag.description !== 'compliance_tags')
      ?.map((tag) => tag.tag_name) || [];

  const handleUserTagAdd = useCallback(
    (tagName: string) => {
      if (selectedAsset?.id) {
        createAssetTag({
          data: {
            asset_id: selectedAsset.id,
            tag_name: tagName,
            source_type: 'user'
          },
          orgId: selectedAsset.organization_id
        });
      }
    },
    [selectedAsset?.id, createAssetTag]
  );

  const handleUserTagRemove = useCallback(
    (tagName: string) => {
      // Find the tag ID from the user_tags array
      const tagToDelete = findTagByName(tagName, user_tags);
      if (tagToDelete) {
        deleteAssetTag(tagToDelete.id);
      }
    },
    [user_tags, deleteAssetTag]
  );

  const handleComplianceTagAdd = useCallback(
    (tagName: string) => {
      if (selectedAsset?.id) {
        createAssetTag({
          data: {
            asset_id: selectedAsset.id,
            tag_name: tagName,
            source_type: 'user',
            description: 'compliance_tags'
          },
          orgId: selectedAsset.organization_id
        });
      }
    },
    [selectedAsset?.id, createAssetTag]
  );

  const handleComplianceTagRemove = useCallback(
    (tagName: string) => {
      // Find the tag ID from the compliance_tags array
      const tagToDelete = findTagByName(tagName, compliance_tags);
      if (tagToDelete) {
        deleteAssetTag(tagToDelete.id);
      }
    },
    [compliance_tags, deleteAssetTag]
  );

  // hook up to api when we know shape of data
  // const selectedAssetRunningServices = useMemo(() => {
  //   // stuff here
  // }, [selectedAsset]);

  // const networkAdapters = externalDetails.map((details:any) => getNetworkAdapters(
  //   (
  //     details as AssetExternalDetailDTO & {
  //       data?: { network_interfaces?: string[] };
  //     }
  //   )?.data?.network_interfaces as string[]
  // ));

  const panelActions = useMemo(
    () => (
      <>
        <Button
          component={Link}
          sx={{ mr: 1 }}
          to={`/asset/${selectedAsset?.id}`}
          variant="outlined"
        >
          Full Details
        </Button>
        <CheckRequirements permissions={[PERMISSIONS.ASSET_UPDATE]}>
          <AssetActionsButton showEdit onEdit={() => setOpenEditDialog(true)} />
        </CheckRequirements>
      </>
    ),
    [selectedAsset?.id]
  );

  const assetNameWithScore = useMemo(
    () => (
      <div className={classes.assetPanelNameContainer}>
        <div>
          <MultiPartScoreBadge
            score={risk_score || 0}
            parts={scorePartsNoTotal(risk_score_details || {})}
            height={30}
            width={30}
            handleClick={() => setAssetRiskScoreDialog(selectedAsset)}
          />
        </div>
        <Typography variant="h6" className={classes.assetPanelName}>
          {asset_name}
        </Typography>
      </div>
    ),
    [risk_score, asset_name, selectedAsset, risk_score_details]
  );

  return (
    <Panel
      {...(notAssetDetailsPage && {
        action: panelActions,
        onClose: handleClose
      })}
      title={notAssetDetailsPage ? assetNameWithScore : 'Asset Details'}
    >
      {description && (
        <AssetPanelItemWrapper
          title="Description"
          tooltipText={
            protectedAttributes.includes('description')
              ? ASSET_TOOLTIP_TEXT
              : undefined
          }
        >
          {description}
        </AssetPanelItemWrapper>
      )}
      {host_isolation_exclusion_reasons.length > 0 && (
        <Box sx={{ pb: 2 }}>
          <KeyValueResponsive
            items={[
              {
                label: 'Response Action Exclusion Matches',
                value: (
                  <Box>
                    {host_isolation_exclusion_reasons.map((reason) => (
                      <Chip
                        className={classes.assetPanelChips}
                        key={reason}
                        label={startCase(
                          formatHostIsolationExclusionReasons([reason])
                        )}
                      />
                    ))}
                  </Box>
                )
              }
            ]}
          />
        </Box>
      )}
      <AssetPanelItemWrapper
        title="Compliance Tags"
        tooltipText={
          protectedAttributes.includes('compliance_tags')
            ? ASSET_TOOLTIP_TEXT
            : undefined
        }
      >
        <InlineEditAutocomplete
          key={selectedAsset?.id}
          value={complianceTagNames}
          availableOptions={COMPLIANCE_OPTIONS}
          placeholder="Add compliance tags..."
          noOptionsText="No compliance tags available"
          onAdd={handleComplianceTagAdd}
          onRemove={handleComplianceTagRemove}
          allowCustomInput={false}
          disabled={!usePermissionsCheck(PERMISSIONS.ASSET_UPDATE)}
        />
      </AssetPanelItemWrapper>
      <AssetPanelItemWrapper title="Tags">
        <InlineEditAutocomplete
          key={selectedAsset?.id}
          value={userTagNames}
          availableOptions={userTagOptions}
          placeholder="Add tags..."
          noOptionsText="No tags available"
          onAdd={handleUserTagAdd}
          onRemove={handleUserTagRemove}
          disabled={!usePermissionsCheck(PERMISSIONS.ASSET_UPDATE)}
          readOnlyValues={ipMappingTagNames}
        />
      </AssetPanelItemWrapper>
      {groupNameTags.length > 0 && (
        <AssetPanelItemWrapper
          title="Group Names"
          tooltipText={
            protectedAttributes.includes('group_names')
              ? ASSET_TOOLTIP_TEXT
              : undefined
          }
        >
          {groupNameTags.map((label: string) => (
            <Chip
              className={classes.assetPanelChips}
              key={label}
              label={label}
            />
          ))}
        </AssetPanelItemWrapper>
      )}
      {notAssetDetailsPage && (
        <Box sx={{ pt: 2 }}>
          <Accordion className={classes.assetPanelAccordion} defaultExpanded>
            <AccordionSummary
              className={classes.assetPanelAccordionSummary}
              expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
              id="panel-details-header"
            >
              <Stack
                direction="row"
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                <Typography className={classes.assetPanelProviderTitle}>
                  Details
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <KeyValueResponsive
                resizable
                items={dataProviderDetailsFields(selectedAsset as Asset)}
              />
            </AccordionDetails>
          </Accordion>

          {externalDetails?.map((item) => (
            <Box
              key={item.provider}
              {...(externalDetails[externalDetails.length - 1] === item
                ? { sx: { pb: 1 } }
                : {})}
            >
              <Accordion
                className={classes.assetPanelAccordion}
                defaultExpanded
              >
                <AccordionSummary
                  className={classes.assetPanelAccordionSummary}
                  expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
                  id={`panel-${item.provider}-header`}
                >
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', display: 'flex' }}
                  >
                    <AssetProviderLogo label={item.provider} />
                    <Typography className={classes.assetPanelProviderTitle}>
                      {
                        ASSET_DATA_PROVIDER_LABELS[
                          item.provider as keyof typeof ASSET_DATA_PROVIDER_LABELS
                        ]
                      }
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <KeyValueResponsive
                    resizable
                    items={dataProviderDetailsFields(
                      item as AssetExternalDetailDTO
                    )}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
        </Box>
      )}
      {/* {networkAdapters.length > 0 && (
        <Stack
          className={classes.assetPanelContentSection}
          direction="column"
          spacing={1}
        >
          <Typography className={classes.assetPanelContentTitle}>
            Network Adapters:
          </Typography>
          <AgGridReact
            columnDefs={NETWORK_ADAPTER_COLUMNS}
            domLayout="autoHeight"
            rowData={networkAdapters}
            rowHeight={30}
            suppressCellFocus
            theme={gridTheme}
          />
        </Stack>
      )} */}
      <Stack
        className={classes.assetPanelContentSection}
        direction="column"
        spacing={1}
      >
        <Typography className={classes.assetPanelContentTitle}>
          Running Services (Last 30 Days):
        </Typography>
        <AgGridReact
          columnDefs={RUNNING_SERVICES_COLUMNS}
          containerStyle={{
            maxHeight: '300px',
            overflow: 'auto',
            height: runningServices.length ? '' : '6rem'
          }}
          defaultColDef={{ suppressHeaderMenuButton: true }}
          domLayout={runningServices.length ? 'autoHeight' : 'normal'}
          headerHeight={32}
          loading={isRunningServicesLoading}
          overlayNoRowsTemplate='<div style="display: flex; align-items: center; justify-content: center; height: 100%;">No active services with open ports detected in the past 30 days</div>'
          rowData={runningServices}
          rowHeight={32}
          suppressCellFocus
          suppressContextMenu
          theme={servicesTableTheme}
        />
      </Stack>
      {openEditDialog && (
        <AssetAddEditDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          type="edit"
        />
      )}
    </Panel>
  );
};

export default AssetPanel;
